import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom, map, tap } from 'rxjs';
import { SectionConfigApiService } from '../../features/data-tables/section-config-api.service';
import { SectionConfigsStore } from '../../features/data-tables/section-configs.store';
import { ProjectService } from '../../shared/services/project/project.service';
import {
  ImpactConfirmationDialog,
  type ImpactConfirmDialogData,
} from '../../shared/common/impact-confirmation-dialog/impact-confirmation-dialog';
import { ConfirmationDialog } from '../../shared/common/confirmation-dialog/confirmation-dialog';
import {
  SectionManagerDialog,
  type SectionManagerDialogAdapter,
  type SectionManagerDialogConfig,
  type SectionManagerItem,
} from '../../shared/common/section-manager-dialog/section-manager-dialog';

@Injectable({ providedIn: 'root' })
export class ManagedDataSectionsUiService {
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly api = inject(SectionConfigApiService);
  private readonly store = inject(SectionConfigsStore);
  private readonly projectService = inject(ProjectService);

  openSectionManagerDialog(): void {
    const adapter: SectionManagerDialogAdapter = {
      loadItems: () =>
        this.api.getAll().pipe(
          map((configs) =>
            configs.map((c) => ({ id: c.id, key: c.key, label: c.label, visible: c.visible, group: c.group })),
          ),
        ),
      createItem: (dto) =>
        this.api.create(dto).pipe(
          tap((created) => this.store.addSection(created)),
          map((c) => ({ id: c.id, key: c.key, label: c.label, visible: c.visible, group: c.group })),
        ),
      updateItem: (id, dto) =>
        this.api
          .update(id, { label: dto.label, visible: dto.visible, order: dto.order, group: dto.group })
          .pipe(tap((updated) => this.store.updateSection(updated))),
      deleteItem: (item) =>
        this.api.remove(item.id, this.projectService.singlePlantId ?? undefined).pipe(
          tap(() => {
            this.store.removeSection(item.id);
            if (this.router.url.includes(item.key)) {
              void this.router.navigate(['/data-management']);
            }
          }),
        ),
      confirmDelete: (item) => this.confirmSectionDelete(item),
    };

    this.dialog.open(SectionManagerDialog, {
      width: '860px',
      data: { adapter, groupOptions: this.store.uniqueGroups() } satisfies SectionManagerDialogConfig,
    });
  }

  private async confirmSectionDelete(item: SectionManagerItem): Promise<boolean> {
    const plantId = this.projectService.singlePlantId;
    if (!plantId) return false;

    const impact = await firstValueFrom(this.api.getShareImpact(item.id, plantId, [], true));
    const hasImpact = impact.dashboards.length > 0 || impact.reports.length > 0 || (impact.metrics?.length ?? 0) > 0;

    if (hasImpact) {
      const dialogData: ImpactConfirmDialogData = { impact, action: 'delete' };
      return firstValueFrom(
        this.dialog.open(ImpactConfirmationDialog, { data: dialogData }).afterClosed(),
      ).then((result) => result ?? false);
    }

    return firstValueFrom(
      this.dialog
        .open(ConfirmationDialog, { data: { confirmButtonType: 'delete' } })
        .afterClosed(),
    ).then((result) => result ?? false);
  }
}
